import { sectionHeading, gameColors, woodPanel, metalPanel, copperButton } from '@/shared/ui/game-theme';
import { GameIcon } from '@/shared/ui/game-icon';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import type { AdventureLogEntry, EquipmentItem, HeroQuest, HeroState } from '@/game/types';
import { InventoryContainer } from '../containers/inventory-container';
import type { HeroResponse } from '@/network/hero.api';
import { HeroStatsContent } from './HeroPanel';
import { AdventureLogEntries } from './AdventureLog';
import { QuestLogList } from './QuestLog';
import { useT, useLocale, type Locale } from '@/i18n';

export type HeroSheetTab = 'stats' | 'character' | 'inventory' | 'biography' | 'journal' | 'quests' | 'settings';

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
  pointerEvents: 'auto',
};

const backdrop: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  backdropFilter: 'blur(4px)',
};

const panel: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 420,
  height: 'min(88vh, 640px)',
  maxHeight: 'min(88vh, 640px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 14,
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backgroundColor: 'rgba(12, 14, 22, 0.94)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.55)',
  overflow: 'hidden',
  ...woodPanel,
};

const header: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  borderBottom: '1px solid #77614b80',
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  fontSize: 15,
  fontWeight: 700,
  color: gameColors.text,
  ...sectionHeading,
};

const closeBtn: CSSProperties = {
  flexShrink: 0,
  background: 'none',
  border: 'none',
  color: gameColors.faint,
  fontSize: 22,
  cursor: 'pointer',
  lineHeight: 1,
  ...copperButton,
  width: 32,
  height: 32,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const tabsRow: CSSProperties = {
  overflowX: 'auto',
  display: 'flex',
  borderBottom: '1px solid #77614b80',
  flexShrink: 0,
};

const tabBtn = (active: boolean): CSSProperties => ({
  flex: '0 0 auto',
  padding: '8px 10px',
  fontSize: 11,
  minWidth: 62,
  whiteSpace: 'nowrap',
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  background: active ? copperButton.background : 'transparent',
  color: active ? gameColors.text : gameColors.muted,
  borderBottom: active ? '2px solid #ba8b5d' : '2px solid transparent',
  WebkitTapHighlightColor: 'transparent',
});

const body: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px 12px 16px',
  fontSize: 12,
  color: gameColors.text,
  WebkitOverflowScrolling: 'touch',
};

const goldBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
  padding: '8px 10px',
  borderRadius: 8,
  background: 'rgba(255, 215, 0, 0.08)',
  border: '1px solid rgba(255, 215, 0, 0.2)',
  fontWeight: 700,
  fontSize: 14,
  ...metalPanel,
  color: gameColors.gold,
};

interface HeroSheetModalProps {
  characterContent: ReactNode;
  connected: boolean;
  onEquipmentUpdated: (hero: HeroResponse) => void;
  biographyContent: ReactNode;
  settingsExtra?: ReactNode;
  open: boolean;
  onClose: () => void;
  initialTab?: HeroSheetTab;
  hero: HeroState;
  nowMs: number;
  equipment: Record<string, EquipmentItem>;
  logEntries: AdventureLogEntry[];
  journalLoading: boolean;
  journalFailed: boolean;
  onLoadJournal: () => void;
  quests: HeroQuest[];
  onQuestClaim: (heroQuestId: number) => void;
  onQuestAbandon: (heroQuestId: number) => void;
  /** Disable claim while hero is dead (HP 0 / death phase). */
  questClaimDisabled?: boolean;
}

export function HeroSheetModal({
  characterContent,
  connected,
  onEquipmentUpdated,
  biographyContent,
  settingsExtra,
  open,
  onClose,
  initialTab = 'character',
  hero,
  nowMs,
  equipment,
  logEntries,
  journalLoading,
  journalFailed,
  onLoadJournal,
  quests,
  onQuestClaim,
  onQuestAbandon,
  questClaimDisabled = false,
}: HeroSheetModalProps) {
  const normalizeTab = (value: HeroSheetTab): HeroSheetTab => value === 'stats' || value === 'biography' ? 'character' : value;
  const [tab, setTab] = useState<HeroSheetTab>(() => normalizeTab(initialTab));
  const [expandedBlocks, setExpandedBlocks] = useState({ stats: true, character: true, biography: true });
  const blockId = useId();
  const toggleBlock = (key: keyof typeof expandedBlocks) => setExpandedBlocks(previous => ({ ...previous, [key]: !previous[key] }));
  const bodyRef = useRef<HTMLDivElement>(null);
  const biographyRef = useRef<HTMLElement>(null);
  const pendingBiographyScroll = useRef(false);
  const tr = useT();
  const { locale, setLocale } = useLocale();

  useEffect(() => {
    pendingBiographyScroll.current = open && initialTab === 'biography';
    if (pendingBiographyScroll.current) setExpandedBlocks(previous => ({ ...previous, biography: true }));
    if (open) setTab(normalizeTab(initialTab));
  }, [open, initialTab]);

  useEffect(() => {
    if (!open || tab !== 'character' || !expandedBlocks.biography || !pendingBiographyScroll.current) return;
    const bodyElement = bodyRef.current, biographyElement = biographyRef.current;
    if (!bodyElement || !biographyElement) return;
    // Scroll only the sheet reader. Do not move the game or outer document,
    // and do not reset the user's scroll when biography data later arrives.
    bodyElement.scrollTop += biographyElement.getBoundingClientRect().top - bodyElement.getBoundingClientRect().top;
    pendingBiographyScroll.current = false;
  }, [open, initialTab, tab, expandedBlocks.biography]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  useEffect(() => { if (open && initialTab === 'journal') onLoadJournal(); }, [open, initialTab, onLoadJournal]);

  if (!open) return null;

  return (
    <div style={overlay}>
      <div style={backdrop} onClick={onClose} aria-hidden />
      <div style={panel} role="dialog" aria-modal aria-labelledby="hero-sheet-title">
        <div className="th-hero-sheet-header" style={header}>
          <span id="hero-sheet-title" style={titleStyle}>
            {hero.name || tr.hero} · {tr.level}.{hero.level}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <button type="button" style={closeBtn} onClick={() => setTab('settings')} aria-label={tr.settings} aria-pressed={tab === 'settings'} title={tr.settings}>
              <GameIcon name="settings" size={16} />
            </button>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
            <GameIcon name="close" />
          </button>
          </div>
        </div>
        <div style={tabsRow}>
          <button type="button" style={tabBtn(tab === 'character')} onClick={() => setTab('character')}>
            {tr.character}
          </button>
          <button type="button" style={tabBtn(tab === 'inventory')} onClick={() => setTab('inventory')}>
            {tr.inventory}
          </button>
          <button type="button" style={tabBtn(tab === 'journal')} onClick={() => { setTab('journal'); onLoadJournal(); }}>

            {tr.journal}
          </button>
          <button type="button" style={tabBtn(tab === 'quests')} onClick={() => setTab('quests')}>
            {tr.quests}
          </button>
        </div>
        <div ref={bodyRef} className="th-hero-sheet-body" style={body}>
          {tab === 'character' && <>
            <CharacterBlock id={`${blockId}-stats`} title={tr.stats} expanded={expandedBlocks.stats} onToggle={() => toggleBlock('stats')}>
              <HeroStatsContent hero={hero} nowMs={nowMs} />
            </CharacterBlock>
            <CharacterBlock id={`${blockId}-character`} title={locale === 'ru' ? 'Характер' : 'Character'} expanded={expandedBlocks.character} onToggle={() => toggleBlock('character')}>
              {characterContent}
            </CharacterBlock>
            <CharacterBlock sectionRef={biographyRef} id={`${blockId}-biography`} title={locale === 'ru' ? 'Биография' : 'Biography'} expanded={expandedBlocks.biography} onToggle={() => toggleBlock('biography')}>
              {biographyContent}
            </CharacterBlock>
          </>}
          {tab === 'inventory' && (
            <>
              <div style={goldBar}>
                <span style={{ fontSize: 18 }}><GameIcon name="coins" color={gameColors.gold} /></span>
                <span>{hero.gold.toLocaleString()} {tr.gold.toLowerCase()}</span>
              </div>
              <InventoryContainer equipment={equipment} equipmentRevision={hero.equipmentRevision ?? 0} heroId={hero.id} connected={connected} onHeroUpdated={onEquipmentUpdated} />
            </>
          )}
          {tab === 'journal' && <>{journalLoading && <p role="status">{locale === 'ru' ? 'Дополняю журнал…' : 'Updating my journal…'}</p>}{journalFailed && <p role="status">{locale === 'ru' ? 'Не удалось получить часть истории.' : 'Some of my history is unavailable.'} <button type="button" onClick={onLoadJournal}>{locale === 'ru' ? 'Повторить' : 'Retry'}</button></p>}<AdventureLogEntries embedded scrollRef={bodyRef} entries={logEntries} /></>}
          {tab === 'quests' && (
            <QuestLogList
              embedded
              quests={quests}
              onClaim={onQuestClaim}
              onAbandon={onQuestAbandon}
              claimDisabled={questClaimDisabled}
            />
          )}
          {tab === 'settings' && (
            <><SettingsContent locale={locale} setLocale={setLocale} tr={tr} />{settingsExtra}</>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Settings Tab ----

const settingRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 0',
  flexWrap: 'wrap',
  gap: 12,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const langBtn = (active: boolean): CSSProperties => ({
  ...copperButton,
  padding: '6px 16px',
  borderRadius: 6,
  border: active ? '2px solid #ba8b5d' : '1px solid rgba(255,255,255,0.15)',
  background: active ? copperButton.background : metalPanel.background,
  color: active ? gameColors.gold : gameColors.muted,
  fontWeight: active ? 700 : 400,
  fontSize: 13,
  cursor: 'pointer',
  transition: 'all 150ms ease',
});

interface SettingsContentProps {
  locale: Locale;
  setLocale: (l: Locale) => void;
  tr: { settings: string; language: string; english: string; russian: string };
}

function SettingsContent({ locale, setLocale, tr }: SettingsContentProps) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: gameColors.text, marginBottom: 12 }}>
        {tr.settings}
      </div>
      <div style={settingRow}>
        <span style={{ color: gameColors.text, fontSize: 13 }}>{tr.language}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            style={langBtn(locale === 'en')}
            onClick={() => setLocale('en')}
          >
            {tr.english}
          </button>
          <button
            type="button"
            style={langBtn(locale === 'ru')}
            onClick={() => setLocale('ru')}
          >
            {tr.russian}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hidden bodies stay mounted so biography drafts and submission state survive collapse. */
function CharacterBlock({ id, title, expanded, onToggle, children, sectionRef }: {
  id: string; title: string; expanded: boolean; onToggle: () => void; children: ReactNode;
  sectionRef?: RefObject<HTMLElement | null>;
}) {
  return <section ref={sectionRef} aria-label={title} style={{ ...metalPanel, marginBottom: 12, overflow: 'hidden' }}>
    <h3 style={{ margin: 0 }}>
      <button type="button" aria-expanded={expanded} aria-controls={id} onClick={onToggle}
        style={{ ...sectionHeading, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minHeight: 42, padding: '10px 12px', background: 'transparent', border: 0, borderBottom: expanded ? '1px solid #77614b80' : 'none', fontSize: 15, textAlign: 'left', cursor: 'pointer' }}>
        {title}<span style={{ display: 'flex', transform: expanded ? 'none' : 'rotate(-90deg)' }}><GameIcon name="down" size={16} /></span>
      </button>
    </h3>
    <div id={id} hidden={!expanded} style={{ padding: 10, display: expanded ? undefined : 'none' }}>{children}</div>
  </section>;
}
